import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { media, layout, remCalc, colorUtils } from '@prisjakt/ui-base';
import { Hideable } from '@prisjakt/ui/hideable';
import AppsIconMDI from 'mdi-react/AppsIcon';
import CrossIconMDI from 'mdi-react/CloseIcon';
import SearchIconMDI from 'mdi-react/MagnifyIcon';
import { Tooltip as TooltipComponent } from 'src/components/tooltip-new';
import Button from '@prisjakt/ui-button';
import UserAvatarComponent from '@prisjakt/ui-user-avatar';
import Viewport from '../../components/viewport';
import AccountMenuComponent from '../account/menu';
import { zindex } from 'src/ui/zindex';

type MobileHeaderProps = {
  mobileSearchOpen?: boolean;
};

const logoHeight = {
  small: '30px',
  medium: '35px',
  large: '38px',
};

// The total value of the corresponding platform variables equal to 100%
const DESKTOP_LOGO_PERCENT_WIDTH = 25;
const DESKTOP_SEARCH_PERCENT_WIDTH = 50;
const DESKTOP_RIGHT_COLUMN_PERCENT_WIDTH = 25;

const TABLET_MENU_BTN_PERCENT_WIDTH = 7;
const TABLET_LOGO_PERCENT_WIDTH = 25;
const TABLET_SEARCH_PERCENT_WIDTH = 50;
const TABLET_RIGHT_COLUMN_PERCENT_WIDTH = 18;

export const height = {
  desktop: layout.headerHeight.desktop.rem,
  mobile: layout.headerHeight.mobile.rem,
  mobileWithSearch: layout.headerHeight.mobileWithSearch.rem,
};

const headerMaxWidth = layout.pageContent.maxWidth;

// The viewport width above which the collapsed sidebar no longer occupies the same horizontal area as the header content
const sidebarOutsideHeaderWidth =
  headerMaxWidth.value + layout.sidebarMenuWidth.desktop.value * 2;

const mobileHeaderHeight = ({ mobileSearchOpen }: MobileHeaderProps) => {
  return mobileSearchOpen
    ? layout.headerHeight.mobileWithSearch.value
    : layout.headerHeight.mobile.value;
};

const scaledMobileHeaderHeight = (props: MobileHeaderProps) =>
  mobileHeaderHeight(props) / layout.headerHeight.mobile.value;

type FixedMarginProps = {} & MobileHeaderProps;
export const FixedMargin = styled.div<FixedMarginProps>(props => [
  {
    display: props.theme.layout === 'raw-mobile' ? 'none' : 'block',
    height: height.desktop,
    transition: 'height 300ms ease',
    willChange: 'height',
  },

  media.smallOnly({
    height: remCalc(mobileHeaderHeight(props)),
  }),
]);

type HeaderBarProps = {} & MobileHeaderProps;
export const HeaderBar = styled.header.attrs({
  'data-test': 'Header',
})<HeaderBarProps>`
  display: ${p => (p.theme.layout === 'raw-mobile' ? 'none' : 'block')};
  background: ${p => p.theme.header.bg};
  width: 100%;
  position: fixed;
  top: 0;
  height: ${height.desktop};
  /* Should be above mega menu */
  z-index: ${zindex.menu.header};

  ${media.smallOnly`
    background: transparent;
    height: auto;
    max-height: ${layout.headerHeight.mobileWithSearch.rem};

    /* Use a pseudo-element for the background in order to be able to transition the
       transform property when the header height changes. This is much smoother than
       e.g. transitioning the actual height of the header. */
    ::before {
      background: ${p => p.theme.header.bg};
      content: '';
      display: block;
      height: ${layout.headerHeight.mobile.rem};
      /* Poke out to the left to be visible in the sidebar view */
      left: -50px;
      position: absolute;
      right: 0;
      top: 0;
      transform: scaleY(${props =>
        scaledMobileHeaderHeight(props as MobileHeaderProps)});
      transform-origin: 0 0;
      transition: transform 0.2s ease-out;
      will-change: transform;
      z-index: -1;
    }
  `}
`;

export const Wrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  max-width: ${headerMaxWidth.rem};
  height: 100%;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  /* Should be above SlidingBox */
  z-index: 2;

  ${media.smallOnly`
    height: ${height.mobile};
  `}

  @media screen and (min-width: ${
    headerMaxWidth.value
  }px) and (max-width: ${sidebarOutsideHeaderWidth}px) {
    &::before {
      content: '';
      display: block;
      width: 25%;
    }
  }
`;

const columnStyles = css`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: 100%;
`;

type MenuItemProps = {
  wideOnTablet?: boolean;
};
export const MenuItem = styled.div<MenuItemProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 50px;
  height: 100%;
  ${props =>
    props.wideOnTablet &&
    media.mediumOnly`
    width: 100px;
  `}
`;

export const MenuColumn = styled.div`
  ${columnStyles};
  width: auto;

  ${media.mediumOnly`
    width: ${TABLET_MENU_BTN_PERCENT_WIDTH}%;
  `}

  ${media.largeUp`
    display: none;
  `};
`;

export const MegaMenuItem = styled(TooltipComponent)`
  height: ${height.desktop};

  @media screen and (min-width: ${sidebarOutsideHeaderWidth + 1}px) {
    left: 0;
    position: fixed;
    top: 0;
  }

  ${media.mediumDown`
    display: none;
  `}
`;

type MegaMenuButtonProps = {
  active?: boolean;
};
export const MegaMenuButton = styled.button<MegaMenuButtonProps>`
  cursor: pointer;
  display: block;
  height: 100%;
  width: ${layout.sidebarMenuWidth.desktop.rem};
  background: ${p => (p.active ? 'white' : 'transparent')};
  color: ${p => (p.active ? p.theme.icons.callToAction : p.theme.header.fg)};
  transition: background-color 0.1s linear, color 0.1s linear;

  &:hover,
  &.focus-visible {
    background: ${p => (p.active ? 'white' : p.theme.buttons.primary.bg)};
  }

  /* Double selector to override aggressive user-agent styles in e.g. Firefox */
  &:focus:focus {
    outline: none;
  }
`;

export const MegaMenuIcon = styled(AppsIconMDI).attrs(p => ({
  color: p.theme.header.fg,
}))`
  fill: currentColor;
`;

export const CloseMegaMenuIcon = styled(CrossIconMDI).attrs(p => ({
  color: p.theme.header.fg,
}))`
  fill: currentColor;
`;

export const LogoColumn = styled.div`
  ${columnStyles}

  /* Special case for a certain interval of viewport widths where we set this whole column as fixed position and aligned to the left.
     This is because we want the mega menu button to align with the sidebar, but it also has to play nicely with the logo when they "collide".
     See also the Wrapper component which adds a pseudo-element at this same interval to reserve space for the column. */
  @media screen and (min-width: ${
    headerMaxWidth.value
  }px) and (max-width: ${sidebarOutsideHeaderWidth}px) {
    position: fixed;
    left: 0;
    height: ${height.desktop};
  }

  ${media.largeUp`
    width: ${DESKTOP_LOGO_PERCENT_WIDTH}%;
  `}

  ${media.mediumOnly`
    width: ${TABLET_LOGO_PERCENT_WIDTH}%;
  `}

  ${media.smallOnly`
    height: ${height.mobile};
    width: auto;
  `}
`;

export const LogoLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 0 10px;

  ${media.mediumOnly`
    margin-left: 10px;
  `};

  ${media.showOutsiderAd`
    margin-left: 0;
  `};

  img {
    height: ${logoHeight.small};

    ${media.mediumUp`
      height: ${logoHeight.medium};
    `};

    ${media.largeUp`
      height: ${logoHeight.large};
    `};
  }

  &.focus-visible {
    background: ${p => p.theme.buttons.primary.bg};
    outline: none;
  }
`;

export const SearchColumn = styled(Viewport)`
  ${columnStyles}
  position: relative;
  align-items: center;

  ${media.largeUp`
    width: ${DESKTOP_SEARCH_PERCENT_WIDTH}%;
    margin-left: 20px;
    margin-right: 20px;
  `}

  ${media.mediumOnly`
    width: ${TABLET_SEARCH_PERCENT_WIDTH}%;
  `}

  ${media.smallOnly`
    width: 100%;
    padding: ${layout.measurements.spacing050};
    padding-top: 0;
    flex-direction: column;
  `}
`;

export const RightColumn = styled.div`
  ${columnStyles}
  
  ${media.largeUp`
    width: ${DESKTOP_RIGHT_COLUMN_PERCENT_WIDTH}%;
  `}

  ${media.mediumOnly`
    width: ${TABLET_RIGHT_COLUMN_PERCENT_WIDTH}%
  `}

  ${media.smallOnly`
    flex: 0 0 50px;
    width: auto;
  `}

  ${media.mediumDown`
    justify-content: flex-end;
  `}
`;

export const PromoText = styled.div(
  props => css`
    ${media.mediumDown`
      display: none;
    `}

    display: flex;
    align-items: center;
    color: ${props.theme.colors.base0};
    margin-left: ${layout.measurements.spacing200};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.4px;
  `
);

export const SearchIcon = styled(SearchIconMDI).attrs(p => ({
  color: p.theme.header.fg,
}))``;

export const SearchBox = styled(Hideable)`
  width: 100%;
  position: relative;
  /* Should be below Wrapper */
  z-index: 1;
`;

export const UserArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: ${layout.measurements.spacing100};
  margin-left: auto;
  position: relative;

  > :not(:last-child) {
    margin-right: ${layout.measurements.spacing100};
  }

  ${media.smallOnly`
    display: none;
  `};
`;

export const MenuButton = styled(Button)`
  background: ${p => p.theme.colors.informative700};
  color: ${p => p.theme.colors.base0};
  width: 40px;
  height: 40px;

  &:hover {
    background: ${p => colorUtils.shadeColor(p.theme.buttons.primary.bg, -20)};
  }
`;

export const AccountMenu = styled(AccountMenuComponent)`
  position: unset;
`;

export const UserAvatar = styled(UserAvatarComponent)`
  background: none;

  &:hover {
    background: none;
  }
`;

export const SearchOverlay = styled.div<{ active: boolean }>(
  p =>
    p.active &&
    css`
      ${media.smallOnly`
        position: fixed;
        bottom: 0;
        top: 0;
        left: 0;
        height: 100vh;
        width: 100%;
        z-index: 900;
        overflow-y: auto;
        background: ${p.theme.bg.primary};
    `};
    `
);
